import { useEffect, useState } from "react";
import { listChangeRequests } from "@/shared/api/changes";
import { listNotifications } from "@/shared/api/comments";
import { listOverrideRequests } from "@/shared/api/overrides";
import { navBadgeLabel } from "./badge-math";
import { hasActionPermission } from "./permission-client";

const BADGE_POLL_MS = 45_000;

/** Poll Inbox unread + Approvals pending for shell/home badges. */
export function useShellBadges(input: {
  companySlug: string | undefined;
  ready: boolean;
  held: readonly string[];
  poll?: boolean;
}): {
  inboxUnread: number;
  approvalsPending: number;
  itemLabel: (id: string, label: string) => string;
} {
  const { companySlug, ready, held, poll = true } = input;
  const [inboxUnread, setInboxUnread] = useState(0);
  const [approvalsPending, setApprovalsPending] = useState(0);
  const canApprove = hasActionPermission(held, "can_approve");

  useEffect(() => {
    if (!companySlug || !ready) return;
    let cancelled = false;

    async function refresh() {
      try {
        const notif = await listNotifications(companySlug!, true);
        if (!cancelled) setInboxUnread(notif.unreadCount);
      } catch {
        /* ignore poll errors */
      }
      if (!canApprove) {
        if (!cancelled) setApprovalsPending(0);
        return;
      }
      try {
        const [overrides, changes] = await Promise.all([
          listOverrideRequests(companySlug!, "pending"),
          listChangeRequests(companySlug!, "pending"),
        ]);
        if (!cancelled) {
          setApprovalsPending(
            (overrides.counts?.pending ?? overrides.requests.length) +
              (changes.counts?.pending ?? changes.requests.length),
          );
        }
      } catch {
        /* ignore poll errors */
      }
    }

    void refresh();
    if (!poll) {
      return () => {
        cancelled = true;
      };
    }
    const id = window.setInterval(() => void refresh(), BADGE_POLL_MS);
    function onFocus() {
      void refresh();
    }
    window.addEventListener("focus", onFocus);
    return () => {
      cancelled = true;
      window.clearInterval(id);
      window.removeEventListener("focus", onFocus);
    };
  }, [companySlug, ready, canApprove, poll]);

  function itemLabel(id: string, label: string): string {
    if (id === "inbox") return navBadgeLabel(label, inboxUnread);
    if (id === "approvals") return navBadgeLabel(label, approvalsPending);
    return label;
  }

  return { inboxUnread, approvalsPending, itemLabel };
}
